import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MoreHorizontal, Trash, Camera, Plus } from "lucide-react";
import { useState } from "react";
import {
  useAddressMedias,
  useDeleteAddressMedia,
  useDeleteMediaItem,
  type AddressMediaFields,
} from "@/api/address-medias";

import {
  CreateAddressMediaDialog,
  FindAddressMediaDialog,
  AddressMediaDetailDialog,
  EMPTY_ADDRESS,
} from "./address-media-dialogs";

export function AddressMediasTable() {
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [findFields, setFindFields] = useState<AddressMediaFields | null>(null);
  const [viewId, setViewId] = useState<string | null>(null);
  const [limit] = useState(50);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteMediaItem, setConfirmDeleteMediaItem] = useState<{
    addressMediaId: string;
    mediaItemId: string;
  } | null>(null);

  const {
    data: addressMediasResponse,
    isLoading,
    error,
    refetch,
  } = useAddressMedias(page, limit);
  const pagination = addressMediasResponse?.pagination;
  const deleteAddressMediaMutation = useDeleteAddressMedia();
  const deleteMediaItemMutation = useDeleteMediaItem();

  const handleDeleteAddressMedia = async (id: string) => {
    try {
      await deleteAddressMediaMutation.mutateAsync(id);
      setConfirmDeleteId(null);
    } catch (error) {
      console.error("Error deleting address media:", error);
    }
  };

  const handleDeleteMediaItem = async (
    addressMediaId: string,
    mediaItemId: string
  ) => {
    try {
      await deleteMediaItemMutation.mutateAsync({
        addressMediaId,
        mediaItemId,
      });
      setConfirmDeleteMediaItem(null);
    } catch (error) {
      console.error("Error deleting media item:", error);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2 mb-4">
        <Button
          variant="outline"
          onClick={() => setFindFields({ ...EMPTY_ADDRESS })}
        >
          Find by address
        </Button>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Address Media
        </Button>
      </div>

      {isLoading ? (
        <p role="status">Loading address media data…</p>
      ) : error ? (
        <div role="alert">
          <p>Address media could not be loaded.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : !addressMediasResponse?.data?.length ? (
        <p className="p-10 text-center">No address media found</p>
      ) : null}

      {!isLoading && !error && !!addressMediasResponse?.data?.length && (
        <>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Address</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Postal Code</TableHead>
                  <TableHead>Media Items</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(addressMediasResponse?.data ?? []).map((addressMedia) => (
                  <TableRow key={addressMedia.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {addressMedia.address?.line1
                            ? [
                                addressMedia.address.line1,
                                addressMedia.address.line2,
                              ]
                                .filter(Boolean)
                                .join(" ")
                            : [
                                addressMedia.streetNumber,
                                addressMedia.streetName,
                              ]
                                .filter(Boolean)
                                .join(" ") || "N/A"}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {addressMedia.address?.city ?? addressMedia.city ?? "N/A"}
                    </TableCell>
                    <TableCell>
                      {addressMedia.address?.province ??
                        addressMedia.state ??
                        "N/A"}
                    </TableCell>
                    <TableCell>
                      {addressMedia.address?.postalCode ??
                        addressMedia.postalCode ??
                        "N/A"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        {(addressMedia.mediaItems ?? []).length > 0 ? (
                          <>
                            {(addressMedia.mediaItems ?? [])
                              .slice(0, 3)
                              .map((mediaItem) => (
                                <div
                                  key={mediaItem.id}
                                  className="relative w-12 h-12 cursor-pointer"
                                  onClick={() =>
                                    mediaItem.url
                                      ? setSelectedImage(mediaItem.url)
                                      : null
                                  }
                                >
                                  <img
                                    src={
                                      mediaItem.url ||
                                      "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0yNCAyNGgtMjR2LTI0aDI0djI0em0tMi0yMmgtMjB2MjBoMjB2LTIwem0tNC4xMTggMTQuMDY0Yy0uMzk5LjQxOS0uODM0LjY0MS0xLjI5OS42NDEtLjQ2NSAwLS45LS4yMjItMS4yOTktLjY0MS0uNDAxLS40MTgtLjYwMS0uOTAyLS42MDEtMS40NTEgMC0uNTM5LjIwMS0xLjAyNC42MDEtMS40NTIuMzk5LS40MTkuODM0LS42NDEgMS4yOTktLjY0MS40NjUgMCAuOS4yMjIgMS4yOTkuNjQxLjQwMS40MTguNjAxLjkwMi42MDEgMS40NTEgMCAuNTM5LS4yMDEgMS4wMjQtLjYwMSAxLjQ1MnptLTEuMjk5LTExLjA2NGMtMy4wMjEgMC01LjYwNiAxLjA2Ny03Ljc1NCAzLjItMi4xNDkgMi4xMzQtMy4yMjMgNC43MDgtMy4yMjMgNy43MjEgMCAzLjAyMiAxLjA3NCA1LjU5NyAzLjIyMyA3LjcyIDIuMTQ4IDIuMTM0IDQuNzMzIDMuMjAxIDcuNzU0IDMuMjAxIDMuMDIgMCA1LjYwNS0xLjA2NyA3Ljc1My0zLjIgMi4xNDktMi4xMzQgMy4yMjMtNC43MDkgMy4yMjMtNy43MjEgMC0zLjAyMi0xLjA3NC01LjU5Ny0zLjIyMy03LjcyMS0yLjE0OC0yLjEzMy00LjczMy0zLjItNy43NTMtMy4yem0wIDIuMTMzYzIuMzk1IDAgNC40NDIuODQ4IDYuMTQzIDIuNTMzIDEuNyAxLjY4NiAyLjU1IDMuNzI1IDIuNTUgNi4xMTcgMCAyLjM5Mi0uODUgNC40MzEtMi41NSA2LjExNy0xLjcwMSAxLjY4NS0zLjc0OCAyLjUzMy02LjE0MyAyLjUzMy0yLjM5NCAwLTQuNDQyLS44NDgtNi4xNDMtMi41MzMtMS43LTEuNjg2LTIuNTUtMy43MjUtMi41NS02LjExNyAwLTIuMzkyLjg1LTQuNDMxIDIuNTUtNi4xMTcgMS43MDEtMS42ODUgMy43NDktMi41MzMgNi4xNDMtMi41MzN6Ii8+PC9zdmc+"
                                    }
                                    alt={mediaItem.key || "Address media"}
                                    className="w-full h-full object-cover rounded-md"
                                    onError={(e) => {
                                      console.error(
                                        `Failed to load image: ${mediaItem.url}`
                                      );
                                      (e.target as HTMLImageElement).src =
                                        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0yNCAyNGgtMjR2LTI0aDI0djI0em0tMi0yMmgtMjB2MjBoMjB2LTIwem0tNC4xMTggMTQuMDY0Yy0uMzk5LjQxOS0uODM0LjY0MS0xLjI5OS42NDEtLjQ2NSAwLS45LS4yMjItMS4yOTktLjY0MS0uNDAxLS40MTgtLjYwMS0uOTAyLS42MDEtMS40NTEgMC0uNTM5LjIwMS0xLjAyNC42MDEtMS40NTIuMzk5LS40MTkuODM0LS42NDEgMS4yOTktLjY0MS40NjUgMCAuOS4yMjIgMS4yOTkuNjQxLjQwMS40MTguNjAxLjkwMi42MDEgMS40NTEgMCAuNTM5LS4yMDEgMS4wMjQtLjYwMSAxLjQ1MnptLTEuMjk5LTExLjA2NGMtMy4wMjEgMC01LjYwNiAxLjA2Ny03Ljc1NCAzLjItMi4xNDkgMi4xMzQtMy4yMjMgNC43MDgtMy4yMjMgNy43MjEgMCAzLjAyMiAxLjA3NCA1LjU5NyAzLjIyMyA3LjcyIDIuMTQ4IDIuMTM0IDQuNzMzIDMuMjAxIDcuNzU0IDMuMjAxIDMuMDIgMCA1LjYwNS0xLjA2NyA3Ljc1My0zLjIgMi4xNDktMi4xMzQgMy4yMjMtNC43MDkgMy4yMjMtNy43MjEgMC0zLjAyMi0xLjA3NC01LjU5Ny0zLjIyMy03LjcyMS0yLjE0OC0yLjEzMy00LjczMy0zLjItNy43NTMtMy4yem0wIDIuMTMzYzIuMzk1IDAgNC40NDIuODQ4IDYuMTQzIDIuNTMzIDEuNyAxLjY4NiAyLjU1IDMuNzI1IDIuNTUgNi4xMTcgMCAyLjM5Mi0uODUgNC40MzEtMi41NSA2LjExNy0xLjcwMSAxLjY4NS0zLjc0OCAyLjUzMy02LjE0MyAyLjUzMy0yLjM5NCAwLTQuNDQyLS44NDgtNi4xNDMtMi41MzMtMS43LTEuNjg2LTIuNTUtMy43MjUtMi41NS02LjExNyAwLTIuMzkyLjg1LTQuNDMxIDIuNTUtNi4xMTcgMS43MDEtMS42ODUgMy43NDktMi41MzMgNi4xNDMtMi41MzN6Ii8+PC9zdmc+";
                                    }}
                                  />
                                </div>
                              ))}
                            {(addressMedia.mediaItems ?? []).length > 3 && (
                              <div className="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center text-gray-500">
                                +{(addressMedia.mediaItems ?? []).length - 3}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="flex items-center">
                            <div className="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center">
                              <Camera className="h-6 w-6 text-gray-400" />
                            </div>
                            <span className="ml-2 text-gray-500">
                              No media items
                            </span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {new Date(addressMedia.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => setViewId(addressMedia.id)}
                          >
                            View details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setConfirmDeleteId(addressMedia.id)}
                            className="text-red-600"
                          >
                            <Trash className="mr-2 h-4 w-4" />
                            Delete Address Media
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination && (
            <div className="flex items-center justify-between px-2">
              <div className="text-sm text-gray-500">
                Showing {(pagination.currentPage - 1) * limit + 1} to{" "}
                {Math.min(
                  pagination.currentPage * limit,
                  pagination.totalCount
                )}{" "}
                of {pagination.totalCount} results
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(pagination.currentPage - 1)}
                  disabled={pagination.currentPage <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(pagination.currentPage + 1)}
                  disabled={pagination.currentPage >= pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <CreateAddressMediaDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onFind={setFindFields}
        onRead={setViewId}
      />
      <FindAddressMediaDialog
        key={JSON.stringify(findFields)}
        open={findFields !== null}
        initialFields={findFields ?? EMPTY_ADDRESS}
        onClose={() => setFindFields(null)}
        onRead={setViewId}
      />
      <AddressMediaDetailDialog id={viewId} onClose={() => setViewId(null)} />

      {/* Image Preview Dialog */}
      <Dialog
        open={!!selectedImage}
        onOpenChange={() => setSelectedImage(null)}
      >
        <DialogContent
          className="sm:max-w-[900px] p-0 overflow-hidden"
          aria-describedby="image-preview-description"
        >
          <DialogHeader>
            <DialogTitle>Image Preview</DialogTitle>
          </DialogHeader>
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/50 text-white z-10 hover:bg-black/70"
              onClick={() => setSelectedImage(null)}
            >
              <Trash className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
            <img
              src={selectedImage ?? ""}
              alt="Media preview"
              className="w-full h-auto max-h-[80vh] object-contain"
              onError={(e) => {
                console.error(`Failed to load image: ${selectedImage}`);
                (e.target as HTMLImageElement).src =
                  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0yNCAyNGgtMjR2LTI0aDI0djI0em0tMi0yMmgtMjB2MjBoMjB2LTIwem0tNC4xMTggMTQuMDY0Yy0uMzk5LjQxOS0uODM0LjY0MS0xLjI5OS42NDEtLjQ2NSAwLS45LS4yMjItMS4yOTktLjY0MS0uNDAxLS40MTgtLjYwMS0uOTAyLS42MDEtMS40NTEgMC0uNTM5LjIwMS0xLjAyNC42MDEtMS40NTIuMzk5LS40MTkuODM0LS42NDEgMS4yOTktLjY0MS40NjUgMCAuOS4yMjIgMS4yOTkuNjQxLjQwMS40MTguNjAxLjkwMi42MDEgMS40NTEgMCAuNTM5LS4yMDEgMS4wMjQtLjYwMSAxLjQ1MnptLTEuMjk5LTExLjA2NGMtMy4wMjEgMC01LjYwNiAxLjA2Ny03Ljc1NCAzLjItMi4xNDkgMi4xMzQtMy4yMjMgNC43MDgtMy4yMjMgNy43MjEgMCAzLjAyMiAxLjA3NCA1LjU5NyAzLjIyMyA3LjcyIDIuMTQ4IDIuMTM0IDQuNzMzIDMuMjAxIDcuNzU0IDMuMjAxIDMuMDIgMCA1LjYwNS0xLjA2NyA3Ljc1My0zLjIgMi4xNDktMi4xMzQgMy4yMjMtNC43MDkgMy4yMjMtNy43MjEgMC0zLjAyMi0xLjA3NC01LjU5Ny0zLjIyMy03LjcyMS0yLjE0OC0yLjEzMy00LjczMy0zLjItNy43NTMtMy4yem0wIDIuMTMzYzIuMzk1IDAgNC40NDIuODQ4IDYuMTQzIDIuNTMzIDEuNyAxLjY4NiAyLjU1IDMuNzI1IDIuNTUgNi4xMTcgMCAyLjM5Mi0uODUgNC40MzEtMi41NSA2LjExNy0xLjcwMSAxLjY4NS0zLjc0OCAyLjUzMy02LjE0MyAyLjUzMy0yLjM5NCAwLTQuNDQyLS44NDgtNi4xNDMtMi41MzMtMS43LTEuNjg2LTIuNTUtMy43MjUtMi41NS02LjExNyAwLTIuMzkyLjg1LTQuNDMxIDIuNTUtNi4xMTcgMS43MDEtMS42ODUgMy43NDktMi41MzMgNi4xNDMtMi41MzN6Ii8+PC9zdmc+";
              }}
            />
            <p id="image-preview-description" className="sr-only">
              Image from address media
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Address Media Confirmation Dialog */}
      <Dialog
        open={!!confirmDeleteId}
        onOpenChange={() => setConfirmDeleteId(null)}
      >
        <DialogContent aria-describedby="delete-confirmation-description">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
          </DialogHeader>
          <p id="delete-confirmation-description">
            Are you sure you want to delete this address media? This action
            cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                confirmDeleteId && handleDeleteAddressMedia(confirmDeleteId)
              }
              disabled={deleteAddressMediaMutation.isPending}
            >
              {deleteAddressMediaMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Media Item Confirmation Dialog */}
      <Dialog
        open={!!confirmDeleteMediaItem}
        onOpenChange={() => setConfirmDeleteMediaItem(null)}
      >
        <DialogContent aria-describedby="delete-media-item-description">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
          </DialogHeader>
          <p id="delete-media-item-description">
            Are you sure you want to delete this media item? This action cannot
            be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setConfirmDeleteMediaItem(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (confirmDeleteMediaItem) {
                  void handleDeleteMediaItem(
                    confirmDeleteMediaItem.addressMediaId,
                    confirmDeleteMediaItem.mediaItemId
                  );
                }
              }}
              disabled={deleteMediaItemMutation.isPending}
            >
              {deleteMediaItemMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
